/**
 * O18 (rodada V2-ORCAMENTO-CONSTRUCAO) — Home do COLABORADOR COMERCIAL.
 *
 * Servido APENAS pra usuários do departamento comercial não-admin (HomePage
 * decide); os demais perfis mantêm o home clássico intacto. Referência:
 * docs/architecture/mockup-home-comercial.html.
 *
 * Blocos: Minhas metas (4 medidores circulares que o colaborador escolhe) ·
 * Meu dia (agenda com horário + "Precisa de você") · Meu pipeline (em
 * andamento por etapa + R$ em aberto) · Minha posição na equipe (ranking,
 * respeitando a config de visibilidade O16).
 *
 * Regras de copy: valores por extenso (nunca "52k"), sem jargão, sem emoji.
 * Tudo clicável — cada widget leva à tela filtrada.
 */
import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { Badge, Button, Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, HelpTip, PageHeader, useToast } from '@/components/ui';
import { useAuth } from '@/contexts/AuthContext';
import { useUiPreference } from '@/hooks/use-ui-preference';
import { cn } from '@/lib/cn';
import { hojeBR, mesAtualBR } from '@/lib/datas-brasilia';
import {
  useCrmAgenda,
  useCrmCards,
  useCrmIndicadores,
  useFases,
  useLeads,
  useMetasProgresso,
  useTarefaActions,
} from '@/lib/crm/crm-store';
import { fmtCent } from '@/pages/comercial/crm-shared';
// CARD-VENDAS-VENDEDOR (21/08): mecanismo geral — se um colaborador do
// comercial for vinculado a um vendedor do Omie, o card aparece aqui também.
import { MinhasVendasWidget } from './widgets/MinhasVendasWidget';

import { formatarData, greeting } from '@/lib/format-date';
// M2 (AJUSTES3): "hoje" no fuso de Brasília — UTC virava amanhã a partir das 21h.
function hojeIso(): string {
  return hojeBR();
}

function labelMeta(m: { meta: { evento: string; metrica: string; servicoNomes?: string[] } }): string {
  const eventos: Record<string, string> = {
    lead_criado: 'Leads criados',
    proposta_gerada: 'Propostas geradas',
    proposta_ganha: m.meta.metrica === 'valor' ? 'Faturamento' : 'Propostas ganhas',
    atividade: 'Atividades',
  };
  const base = eventos[m.meta.evento] ?? m.meta.evento;
  // A4: meta pode ter VÁRIOS serviços — até 2 nomes; 3+ vira "N serviços".
  const nomes = m.meta.servicoNomes ?? [];
  if (nomes.length === 0) return base;
  return `${base} — ${nomes.length <= 2 ? nomes.join(' + ') : `${nomes.length} serviços`}`;
}

export function HomeComercial() {
  const { user } = useAuth();
  const navigate = useNavigate();
  // M2 (AJUSTES3): mês corrente no fuso de Brasília (UTC virava o mês seguinte às 21h).
  const mesAno = mesAtualBR();

  const { data: metasData } = useMetasProgresso(mesAno);
  const { data: agenda } = useCrmAgenda(mesAno);
  const { data: cards = [] } = useCrmCards({});
  const { data: leads = [] } = useLeads({});
  const { data: fases = [] } = useFases();
  const { data: indicadores } = useCrmIndicadores({ mes: mesAno });

  // ── Minhas metas: o colaborador escolhe as 4 do home (preferência no
  //    servidor — regra zero-localStorage). Menos de 4 metas = mostra as que
  //    existem. Realizado = a fatia DO COLABORADOR quando a meta quebra por
  //    vendedor; senão o total.
  const [escolhidas, setEscolhidas] = useUiPreference<string[]>('home.comercial.metas', []);
  const [escolhendo, setEscolhendo] = useState(false);

  // `?? []` cru cria um array NOVO a cada render, e este valor entra nas deps do
  // useMemo abaixo — o memo nunca reaproveitava nada.
  const metas = useMemo(() => metasData?.metas ?? [], [metasData]);
  const metasDoHome = useMemo(() => {
    const porId = new Map(metas.map((m) => [m.meta.id, m]));
    const escolhidasValidas = escolhidas.map((id) => porId.get(id)).filter(Boolean) as typeof metas;
    const base = escolhidasValidas.length > 0 ? escolhidasValidas : metas;
    return base.slice(0, 4);
  }, [metas, escolhidas]);

  const medidores = metasDoHome.map((m) => {
    const minha = m.porVendedor.find((v) => v.userId === user?.id);
    const realizado = m.meta.escopo === 'por_vendedor' && minha ? minha.realizado : m.realizadoTotal;
    const alvo = m.meta.escopo === 'por_vendedor' ? m.alvoEfetivo : m.alvoEquipe;
    const pct = alvo > 0 ? Math.min(100, Math.round((realizado / alvo) * 100)) : 0;
    const emValor = m.meta.metrica === 'valor';
    return {
      id: m.meta.id,
      label: labelMeta(m),
      pct,
      noAlvo: alvo > 0 && realizado >= alvo,
      detalhe: emValor ? `${fmtCent(realizado)} de ${fmtCent(alvo)}` : `${realizado} de ${alvo}`,
    };
  });
  const noAlvo = medidores.filter((m) => m.noAlvo).length;

  // ── Meu dia: agenda com horário (O11) + alertas "Precisa de você".
  const [faixaAgenda, setFaixaAgenda] = useState<'hoje' | 'semana' | 'mes'>('hoje');
  const hoje = hojeIso();
  // M2 (AJUSTES3): +7 dias, mas a DATA é derivada no fuso de Brasília.
  const fimSemana = hojeBR(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000));
  // F1 (COMERCIAL-AJUSTES): a agenda junta as tarefas do sistema com os
  // EVENTOS do Google Agenda do usuário (dedupados no backend por
  // google_event_id — quem virou tarefa não aparece duas vezes).
  // CA-5: concluir direto da lista — a tarefa some do filtro !concluida.
  const tarefaAcoes = useTarefaActions();
  const toast = useToast();
  async function concluirDaAgenda(t: { id: string; cardId: string | null; leadId: string | null }) {
    try {
      await tarefaAcoes.concluir.mutateAsync({ atvId: t.id, cardId: t.cardId, leadId: t.leadId });
      toast.show('Tarefa concluída.');
    } catch {
      toast.show('Não foi possível concluir a tarefa.', { variant: 'error' });
    }
  }

  const tarefas = [
    ...(agenda?.tarefas ?? [])
      .filter((t) => !t.concluida)
      .map((t) => ({
        id: t.id,
        titulo: t.titulo,
        data: t.data,
        cardId: t.cardId,
        leadId: t.leadId,
        origem: 'sistema' as const,
      })),
    ...(agenda?.eventosGoogle ?? []).map((e) => ({
      id: `gev-${e.id}`,
      titulo: e.titulo,
      data: e.inicio,
      cardId: null as string | null,
      leadId: null as string | null,
      origem: 'google' as const,
    })),
  ]
    .filter((t) => {
      const d = t.data.slice(0, 10);
      if (faixaAgenda === 'hoje') return d === hoje;
      if (faixaAgenda === 'semana') return d >= hoje && d <= fimSemana;
      return true;
    })
    .sort((a, b) => a.data.localeCompare(b.data));

  const abertos = cards.filter((c) => c.ativo && !c.fechadoEm);
  // TRES-DECISOES (31/08): "sem ação" é o que o SERVIDOR diz (sem-acao.ts),
  // e ele passou a olhar só a tarefa agendada. A tela lia o texto livre, que
  // saiu — e que, mesmo antes, discordava do selo do funil.
  const semAcao = abertos.filter((c) => c.semAcao);
  const parados = abertos.filter((c) => c.diasParado >= 7);
  const leadsQualificar = leads.filter((l) => l.ativo && l.statusSlug !== 'qualificado' && l.statusSlug !== 'descartado');
  const tarefasHoje = (agenda?.tarefas ?? []).filter((t) => !t.concluida && t.data.slice(0, 10) === hoje);

  // ── Meu pipeline: em andamento por etapa + R$ em aberto.
  const fasesAbertas = fases.filter((f) => f.tipo === 'aberta');
  const porEtapa = fasesAbertas.map((f) => ({
    fase: f,
    qtd: abertos.filter((c) => c.faseId === f.id).length,
  }));
  const maxEtapa = Math.max(1, ...porEtapa.map((e) => e.qtd));
  const valorAberto = abertos.reduce(
    (a, c) => a + (c.valorPontual ?? 0) + (c.valorMensal ?? 0) + (c.valorMensalSoftware ?? 0),
    0,
  );

  const ranking = indicadores?.ranking ?? [];
  // #484: a data e a saudação seguem o relógio de Brasília, não o do computador.
  const dataLonga = formatarData(new Date(), { weekday: 'long', day: 'numeric', month: 'long' }).replace(
    /^./,
    (c) => c.toUpperCase(),
  );
  const saudacao = greeting();

  return (
    <div className="space-y-6">
      {/* FASE-C: PageHeader canônico — a data vira o crumb, a saudação é o h1. */}
      <PageHeader
        crumb={dataLonga}
        title={`${saudacao}, ${user?.name?.split(' ')[0] ?? ''}`}
        className="mb-0"
      />

      {/* CARD-VENDAS-VENDEDOR: null pra quem não tem vínculo com o Omie. */}
      <MinhasVendasWidget />

      {/* Minhas metas */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs font-semibold uppercase tracking-wider text-ink-3 dark:text-ink-dark-3 inline-flex items-center gap-1.5">
            Minhas metas no Home {medidores.length > 0 ? `· ${noAlvo} de ${medidores.length} no alvo` : ''}
            <HelpTip texto="Cada círculo mostra quanto da meta do mês você já realizou. Se houver mais de 4 metas, dá pra escolher quais acompanhar aqui." />
          </p>
          <div className="flex gap-3">
            {metas.length > 4 && (
              <button type="button" className="text-xs font-semibold text-red hover:underline" onClick={() => setEscolhendo(true)}>
                Escolher as 4 do Home
              </button>
            )}
            <Link to="/admin/comercial/metas" className="text-xs font-semibold text-red hover:underline">
              Ver todas
            </Link>
          </div>
        </div>
        <div className="bg-paper-surface dark:bg-paper-dark-surface border border-line dark:border-line-dark rounded-xl p-4">
          {medidores.length === 0 ? (
            <p className="text-sm text-ink-3 dark:text-ink-dark-3">
              Nenhuma meta configurada ainda — as metas aparecem aqui assim que forem criadas em
              Comercial, na tela Metas.
            </p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {medidores.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => navigate('/admin/comercial/indicadores')}
                  className="text-center group"
                >
                  <div className="relative w-[104px] h-[104px] mx-auto">
                    <svg viewBox="0 0 104 104" className="w-[104px] h-[104px]">
                      <circle cx="52" cy="52" r="44" fill="none" strokeWidth="9" className="stroke-line-soft dark:stroke-line-soft-dark" />
                      <circle
                        cx="52"
                        cy="52"
                        r="44"
                        fill="none"
                        strokeWidth="9"
                        strokeLinecap="round"
                        strokeDasharray={276.5}
                        strokeDashoffset={276.5 * (1 - m.pct / 100)}
                        transform="rotate(-90 52 52)"
                        className={m.noAlvo ? 'stroke-success' : 'stroke-red'}
                      />
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      {/* FASE-C: número em SF Mono tabular (Oswald só em display/título). */}
                      <span className="font-mono text-xl font-medium tabular-nums text-ink-1 dark:text-ink-dark-1">
                        {m.pct}%
                      </span>
                      <span className="text-xs font-semibold text-ink-3 dark:text-ink-dark-3">da meta</span>
                    </div>
                  </div>
                  <p className="text-sm font-semibold mt-2 text-ink-1 dark:text-ink-dark-1 group-hover:text-red">
                    {m.label}
                  </p>
                  <p className="font-mono text-xs tabular-nums text-ink-3 dark:text-ink-dark-3">{m.detalhe}</p>
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Meu dia */}
      <section>
        <p className="text-xs font-semibold uppercase tracking-wider text-ink-3 dark:text-ink-dark-3 mb-3">
          Meu dia
        </p>
        <div className="grid lg:grid-cols-[1.55fr_1fr] gap-4 items-start">
          <div className="bg-paper-surface dark:bg-paper-dark-surface border border-line dark:border-line-dark rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-ink-1 dark:text-ink-dark-1 inline-flex items-center gap-1.5">
                Minha agenda
                <HelpTip texto="Junta as tarefas do sistema com os eventos do seu Google Agenda. Os itens do Google aparecem marcados e são só leitura aqui. Nas tarefas do sistema, o quadradinho à esquerda conclui a tarefa na hora — ela sai da agenda e o lembrete não dispara mais." />
              </h3>
              <div className="inline-flex bg-line-soft dark:bg-line-soft-dark border border-line dark:border-line-dark rounded-lg p-0.5 gap-0.5">
                {(
                  [
                    ['hoje', 'Hoje'],
                    ['semana', 'Semana'],
                    ['mes', 'Mês'],
                  ] as const
                ).map(([v, label]) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => setFaixaAgenda(v)}
                    className={cn(
                      'px-3 py-1 rounded-md text-xs font-semibold transition-colors',
                      faixaAgenda === v
                        ? 'bg-red text-white'
                        : 'text-ink-3 dark:text-ink-dark-3 hover:text-ink-1 dark:hover:text-ink-dark-1',
                    )}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
            {tarefas.length === 0 ? (
              <p className="text-sm text-ink-3 dark:text-ink-dark-3 py-3">
                Nada agendado {faixaAgenda === 'hoje' ? 'para hoje' : faixaAgenda === 'semana' ? 'para esta semana' : 'neste mês'}.
              </p>
            ) : (
              tarefas.slice(0, 8).map((t) => {
                const temHora = t.data.includes('T');
                const dia = t.data.slice(0, 10);
                const [, mm, dd] = dia.split('-');
                const doGoogle = t.origem === 'google';
                return (
                  <div
                    key={t.id}
                    className="flex items-center gap-2 py-2.5 border-b border-line-soft dark:border-line-soft-dark last:border-b-0"
                  >
                    {/* CA-5: concluir sem sair do início (só tarefas do sistema). */}
                    {!doGoogle && (
                      <button
                        type="button"
                        title="Concluir tarefa"
                        aria-label={`Concluir a tarefa ${t.titulo}`}
                        data-testid={`home-tarefa-concluir-${t.id}`}
                        disabled={tarefaAcoes.concluir.isPending}
                        onClick={() => concluirDaAgenda(t)}
                        className="w-5 h-5 shrink-0 rounded border-2 border-line dark:border-line-dark hover:border-success hover:bg-success/10 disabled:opacity-50"
                      />
                    )}
                    <button
                      type="button"
                      disabled={doGoogle}
                      onClick={() =>
                        t.cardId
                          ? navigate(`/admin/comercial/funil?abrirCard=${encodeURIComponent(t.cardId)}`)
                          : !doGoogle && navigate('/admin/comercial/leads')
                      }
                      className={cn('flex-1 min-w-0 flex items-center gap-3 text-left group', doGoogle && 'pl-7')}
                    >
                      {/* FASE-C: hora/data em SF Mono tabular; tamanhos na escala (14px). */}
                      <span className="font-mono text-sm font-medium tabular-nums text-red dark:text-red-glow w-14 shrink-0">
                        {temHora ? t.data.slice(11, 16) : `${dd}/${mm}`}
                      </span>
                      <span
                        className={cn(
                          'text-sm font-semibold truncate',
                          doGoogle
                            ? 'text-ink-2 dark:text-ink-dark-2'
                            : 'text-ink-1 dark:text-ink-dark-1 group-hover:text-red',
                        )}
                      >
                        {t.titulo}
                      </span>
                      {doGoogle && (
                        <span className="text-xs font-semibold uppercase tracking-wide text-ink-3 dark:text-ink-dark-3 shrink-0">
                          Google Agenda
                        </span>
                      )}
                    </button>
                  </div>
                );
              })
            )}
          </div>

          <div className="bg-paper-surface dark:bg-paper-dark-surface border border-line dark:border-line-dark rounded-xl p-4">
            <h3 className="text-sm font-semibold text-ink-1 dark:text-ink-dark-1 mb-2 inline-flex items-center gap-1.5">
              Precisa de você
              <HelpTip texto="Pendências que merecem atenção agora: propostas paradas ou sem próxima ação, leads pra qualificar e tarefas de hoje. Toque num item pra ir direto à tela." />
            </h3>
            {(
              [
                {
                  n: semAcao.length,
                  label: 'Propostas sem próxima ação',
                  tone: 'bg-red-soft dark:bg-red-soft-dark text-red',
                  to: '/admin/comercial/funil',
                },
                {
                  n: parados.length,
                  label: 'Paradas há mais de 7 dias',
                  tone: 'bg-warning/10 text-warning',
                  to: '/admin/comercial/funil',
                },
                {
                  n: leadsQualificar.length,
                  label: 'Leads a qualificar',
                  tone: 'bg-line-soft dark:bg-line-soft-dark text-ink-2 dark:text-ink-dark-2',
                  to: '/admin/comercial/leads',
                },
                {
                  n: tarefasHoje.length,
                  label: 'Tarefas para hoje',
                  tone: 'bg-success/10 text-success',
                  to: '/admin/comercial/funil?view=calendario',
                },
              ] as const
            ).map((a) => (
              <button
                key={a.label}
                type="button"
                onClick={() => navigate(a.to)}
                className="w-full flex items-center gap-3 py-2.5 border-b border-line-soft dark:border-line-soft-dark last:border-b-0 text-left group"
              >
                <span
                  className={cn(
                    'w-9 h-9 rounded-lg flex items-center justify-center font-mono font-medium tabular-nums text-base shrink-0',
                    a.tone,
                  )}
                >
                  {a.n}
                </span>
                <span className="flex-1 text-sm font-semibold text-ink-1 dark:text-ink-dark-1 group-hover:text-red">
                  {a.label}
                </span>
                <span aria-hidden className="text-ink-3 dark:text-ink-dark-3">
                  ›
                </span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Meu pipeline + posição */}
      <section>
        <p className="text-xs font-semibold uppercase tracking-wider text-ink-3 dark:text-ink-dark-3 mb-3">
          Meu pipeline
        </p>
        <div className="grid lg:grid-cols-2 gap-4 items-start">
          <div className="bg-paper-surface dark:bg-paper-dark-surface border border-line dark:border-line-dark rounded-xl p-4">
            <h3 className="text-sm font-semibold text-ink-1 dark:text-ink-dark-1 mb-3 inline-flex items-center gap-1.5">
              Em andamento por etapa
              <HelpTip texto="Suas propostas abertas em cada etapa do funil e, abaixo, o valor total em negociação. Toque numa barra pra abrir o Funil." />
            </h3>
            {porEtapa.every((e) => e.qtd === 0) ? (
              <p className="text-sm text-ink-3 dark:text-ink-dark-3">Nenhuma proposta em andamento.</p>
            ) : (
              <div className="space-y-2">
                {porEtapa.map((e) => (
                  <button
                    key={e.fase.id}
                    type="button"
                    onClick={() => navigate('/admin/comercial/funil')}
                    className="w-full flex items-center gap-3 group"
                  >
                    <span className="w-[104px] shrink-0 text-left text-sm font-semibold text-ink-1 dark:text-ink-dark-1 truncate">
                      {e.fase.nome}
                    </span>
                    <span className="flex-1 h-7 rounded-lg bg-line-soft dark:bg-line-soft-dark relative overflow-hidden group-hover:ring-2 group-hover:ring-red-soft">
                      <span
                        className="absolute inset-y-0 left-0 bg-red rounded-lg"
                        style={{ width: `${(e.qtd / maxEtapa) * 100}%` }}
                      />
                      <span className="absolute inset-y-0 right-2.5 flex items-center font-mono text-xs font-medium tabular-nums text-ink-1 dark:text-ink-dark-1">
                        {e.qtd}
                      </span>
                    </span>
                  </button>
                ))}
              </div>
            )}
            <p className="text-sm text-ink-2 dark:text-ink-dark-2 mt-3">
              Valor em aberto:{' '}
              <b className="font-mono font-medium tabular-nums text-ink-1 dark:text-ink-dark-1">
                {fmtCent(valorAberto)}
              </b>{' '}
              em {abertos.length} proposta{abertos.length === 1 ? '' : 's'}
            </p>
          </div>

          <div className="bg-paper-surface dark:bg-paper-dark-surface border border-line dark:border-line-dark rounded-xl p-4">
            <h3 className="text-sm font-semibold text-ink-1 dark:text-ink-dark-1 mb-3">Minha posição na equipe</h3>
            {ranking.length === 0 ? (
              <p className="text-sm text-ink-3 dark:text-ink-dark-3">
                O ranking aparece conforme a configuração de visibilidade definida pelo gestor.
              </p>
            ) : (
              <>
                {ranking.slice(0, 5).map((r, i) => (
                  <button
                    key={r.userId}
                    type="button"
                    onClick={() => navigate('/admin/comercial/indicadores')}
                    className={cn(
                      'w-full flex items-center gap-3 py-2 text-left group',
                      r.userId === user?.id && 'bg-red-soft/40 dark:bg-red-soft-dark/40 rounded-lg px-2 -mx-2',
                    )}
                  >
                    <span
                      className={cn(
                        'w-6 h-6 rounded-full flex items-center justify-center font-mono text-xs font-medium tabular-nums shrink-0',
                        i === 0 ? 'bg-red text-white' : 'bg-line-soft dark:bg-line-soft-dark text-ink-3 dark:text-ink-dark-3',
                      )}
                    >
                      {i + 1}
                    </span>
                    <span className="flex-1 text-sm font-semibold text-ink-1 dark:text-ink-dark-1 group-hover:text-red truncate">
                      {r.nome}
                      {r.userId === user?.id ? ' (você)' : ''}
                    </span>
                    <span className="font-mono text-sm font-medium tabular-nums text-ink-2 dark:text-ink-dark-2 shrink-0">
                      {r.metaPct != null ? `${r.metaPct}% da meta` : fmtCent(r.pontual)}
                    </span>
                  </button>
                ))}
                <p className="text-xs text-ink-3 dark:text-ink-dark-3 mt-2.5">
                  O ranking aparece conforme a configuração de visibilidade definida pelo gestor.
                </p>
              </>
            )}
          </div>
        </div>
      </section>

      {/* Dialog: escolher as 4 metas do home */}
      <Dialog open={escolhendo} onOpenChange={(o) => !o && setEscolhendo(false)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Escolher as metas do home</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-ink-2 dark:text-ink-dark-2 mb-3">
            Marque até 4 metas para acompanhar nos medidores do início.
          </p>
          <div className="space-y-2">
            {metas.map((m) => {
              const marcada = escolhidas.includes(m.meta.id);
              const cheia = !marcada && escolhidas.length >= 4;
              return (
                <label
                  key={m.meta.id}
                  className={cn(
                    'flex items-center gap-3 rounded-lg border px-3 py-2.5 text-sm font-semibold cursor-pointer',
                    marcada ? 'border-red bg-red-soft/30 dark:bg-red-soft-dark/30' : 'border-line dark:border-line-dark',
                    cheia && 'opacity-50 cursor-not-allowed',
                  )}
                >
                  <input
                    type="checkbox"
                    checked={marcada}
                    disabled={cheia}
                    onChange={() =>
                      setEscolhidas(
                        marcada ? escolhidas.filter((id) => id !== m.meta.id) : [...escolhidas, m.meta.id],
                      )
                    }
                  />
                  <span className="flex-1 text-ink-1 dark:text-ink-dark-1">{labelMeta(m)}</span>
                  <Badge variant="neutral">{m.meta.escopo === 'por_vendedor' ? 'por vendedor' : 'total'}</Badge>
                </label>
              );
            })}
          </div>
          <DialogFooter>
            <Button size="sm" onClick={() => setEscolhendo(false)}>
              Pronto
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
